import {Field} from 'formik';

import {
    FormikNumberInput,
    FormikSelect,
    FormikTextInput,
    PlatformFieldset
} from '@flexinet/ui-components';
import {Stack} from "@mantine/core";
import {FormikList} from "../../../components/FormikList";
import {BillablePeriod, RatePlanStatus} from "../types.ts";
import {BillableUnitsDropdown} from "../../billable-units/inputs/BillableUnitsDropdown.tsx";


export const FormComponent = () => {

    return (
        <Stack gap={'lg'}>
            <PlatformFieldset legend={'Rate Plan'}>
                <Field
                    name='name'
                    label='Name'
                    description='Please enter the ratePlans name'
                    component={FormikTextInput}
                    withAsterisk
                    my={'xl'}
                />

                {/* FNP-802: the billable unit is now a real record (meter + conversion
                    factor), selected from the Billable Units module — not a hardcoded
                    label list backed by nothing. The tier bands below are counted in
                    THIS unit, so it has to be a real reference. */}
                <Field
                    name='billableUnitId'
                    label='Billable Unit'
                    description='The unit this plan prices. Charge tiers below are counted in this unit.'
                    component={BillableUnitsDropdown}
                    withAsterisk
                    my={'xl'}
                />

                <Field
                    name='billablePeriod'
                    label='Billable Period'
                    description='Please enter the billable period'
                    component={FormikSelect}
                    data={[{
                        value: BillablePeriod.MONTH,
                        label: 'Month'
                    }, {
                        value: BillablePeriod.QUARTER,
                        label: 'Quarter'
                    }, {
                        value: BillablePeriod.YEAR,
                        label: 'Year'
                    }]}
                    withAsterisk
                    my={'xl'}
                />

            </PlatformFieldset>

            {/* FNP-834: RatePlan was the only catalog entity with no lifecycle — nothing
                stopped a new subscription selecting a superseded or half-built plan.
                Status is AUTHORITATIVE, the dates SCHEDULE. Selectability is checked ONLY
                at subscription-create: an existing subscription keeps billing on its terms
                even after its plan retires or expires (the window gates ENTRY, not
                continuation). */}
            <PlatformFieldset legend={'Lifecycle'}>

                <Field
                    name='status'
                    label='Status'
                    description='DRAFT is not sellable. ACTIVE is selectable for new subscriptions. RETIRED withdraws it from sale without deleting it — existing subscriptions are unaffected.'
                    component={FormikSelect}
                    data={[{
                        value: RatePlanStatus.DRAFT,
                        label: 'Draft'
                    }, {
                        value: RatePlanStatus.ACTIVE,
                        label: 'Active'
                    }, {
                        value: RatePlanStatus.RETIRED,
                        label: 'Retired'
                    }]}
                    my={'xl'}
                />

                <Field
                    name='effectiveFrom'
                    label='Effective From'
                    description='When this plan becomes selectable. ISO-8601, e.g. 2026-08-01T00:00:00.000Z.'
                    component={FormikTextInput}
                    placeholder='2026-08-01T00:00:00.000Z'
                    my={'xl'}
                />

                <Field
                    name='effectiveTo'
                    label='Effective To'
                    description='Leave BLANK for open-ended (the common case). Setting a past date stops NEW subscriptions selecting this plan; it does not change existing ones.'
                    component={FormikTextInput}
                    placeholder='blank = open-ended'
                    my={'xl'}
                />

            </PlatformFieldset>

            <PlatformFieldset legend={'Tiers'}>

                <Field
                    name='chargeTiers'
                    label='Charge Tiers'
                    // The rate is stored in MINOR UNITS (cents) — same as Price.amount and
                    // Stripe. Saying so here is the fix for a real money-entry trap: typing
                    // 0.50 meaning fifty cents was rejected with the opaque AppSync error
                    // "Variable 'rate' has an invalid value", and typing 50 meaning fifty
                    // DOLLARS would have silently charged 100x too little.
                    description='Rate is in CENTS per billable unit — enter 50 for $0.50, 1500 for $15.00. Whole numbers only.'
                    component={FormikList}
                    getRowElements={() => {
                        return [{
                            // FNP-836: the bands are Int on the API (were String! with no
                            // validation anywhere, while `rate` beside them was already
                            // numeric). Bands are INCLUSIVE on both ends: a quantity q falls
                            // in a tier iff lowerBand <= q <= upperBand, so the next tier's
                            // Lower Band must be this tier's Upper Band + 1. The FIRST tier
                            // must start at 1 — quantity 0 bills nothing.
                            name: 'lowerBand',
                            placeholder: 'Lower Band',
                            component: FormikNumberInput,
                            allowDecimal: false,
                            min: 1,
                            step: 1,
                        }, {
                            // Leave BLANK on the top tier to mean UNBOUNDED — deliberately
                            // not a large sentinel number.
                            name: 'upperBand',
                            placeholder: 'Upper Band (blank = unbounded)',
                            component: FormikNumberInput,
                            allowDecimal: false,
                            min: 1,
                            step: 1,
                        }, {
                            name: 'rate',
                            placeholder: 'Rate (cents)',
                            component: FormikNumberInput,
                            // Reject a fractional cent at the FORM rather than letting it
                            // reach AppSync as an unreadable variable error.
                            allowDecimal: false,
                            min: 0,
                            step: 1,
                        }]
                    }}
                    my={'xl'}
                />

            </PlatformFieldset>

        </Stack>
    );
};
